/**
 * SegmentedTabs — segmentert kontroll (#152, mangler i MAPPING.md §3).
 *
 * Et spor med pill-formede valg; det aktive valget er fylt. To varianter:
 *   - `variant="tabs"` (standard): knapper i en `radiogroup`, ett valgt
 *     (`role="radio"` + `aria-checked`), slik ThemeToggle og Meg-skissen har
 *     det. For å bytte visning eller verdi på samme side.
 *   - `variant="links"`: lenker i en `<nav>`, aktiv med `aria-current="page"`
 *     (React Routers NavLink). For under-fanene Premier/Plan/Familie, der hver
 *     fane er en egen rute.
 *
 * To toner:
 *   - `default`: spor `bg-tab-bg`, aktiv `bg-solid text-on-solid`, ellers
 *     `text-ink`.
 *   - `panel`: på datopanelet, der teksten alltid er mørk — spor
 *     `bg-on-panel/10`, aktiv `bg-on-panel text-on-panel-solid`, ellers
 *     `text-on-panel`.
 *
 * Valgene er 44 px høye (skissene har 40 på panelet, men mål skal være
 * ≥ 44 px) og deler bredden likt. En lenke med `wide` får halvannen andel
 * (Premier-skissens `flex: 1.5` for «Belønninger», #156). Aktivt valg er 800,
 * de andre 700.
 *
 * En lenke med `active` avgjør selv om den er aktiv, i stedet for NavLink sin
 * stisammenligning. Familie-fanene (#159) skiller seg bare i `?fane=`, som
 * NavLink ikke ser på.
 *
 * `dense` (#159, steg 9d): nesten uten sidepadding, som skissenes knapper
 * (`flex: 1`, ingen padding). Temavelgeren i Meg trenger det for at «Etter
 * solen» med ikon skal få plass på én linje på 390 px; på smalere skjermer
 * brytes etiketten innenfor pillen i stedet for å flyte over nabovalget.
 */
import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router';

type Tone = 'default' | 'panel';

const TRACK: Record<Tone, string> = {
  default: 'bg-tab-bg',
  panel: 'bg-on-panel/10',
};

const ITEM_ON: Record<Tone, string> = {
  default: 'bg-solid text-on-solid font-extrabold',
  panel: 'bg-on-panel text-on-panel-solid font-extrabold',
};

const ITEM_OFF: Record<Tone, string> = {
  default: 'text-ink font-bold',
  panel: 'text-on-panel font-bold',
};

const ITEM_BASE =
  'flex h-11 min-w-0 items-center justify-center gap-1.5 rounded-pill text-control transition-colors';
const PAD = { normal: 'px-3', dense: 'px-1 text-center leading-tight' } as const;

export interface SegmentedOption<V extends string> {
  value: V;
  label: string;
  icon?: ReactNode;
  testId?: string;
}

export interface SegmentedLink {
  to: string;
  label: string;
  /** Aktiv bare ved nøyaktig treff (NavLink `end`). Standard true. */
  end?: boolean;
  /** Halvannen andel av bredden, for en lang etikett. */
  wide?: boolean;
  /** Overstyrer NavLink sin treffsjekk, f.eks. når fanen ligger i søkestrengen. */
  active?: boolean;
  testId?: string;
}

interface CommonProps {
  /** Tilgjengelig navn på gruppen. */
  label: string;
  /** Valgene nesten uten sidepadding. */
  dense?: boolean;
  tone?: Tone;
  className?: string;
  'data-testid'?: string;
}

interface TabsProps<V extends string> extends CommonProps {
  variant?: 'tabs';
  options: readonly SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
}

interface LinksProps extends CommonProps {
  variant: 'links';
  links: readonly SegmentedLink[];
}

export type SegmentedTabsProps<V extends string> = TabsProps<V> | LinksProps;

export function SegmentedTabs<V extends string>(props: SegmentedTabsProps<V>): ReactNode {
  const tone = props.tone ?? 'default';
  const base = `${ITEM_BASE} ${props.dense ? PAD.dense : PAD.normal}`;
  const trackClass = ['flex gap-1 rounded-pill p-1', TRACK[tone], props.className]
    .filter(Boolean)
    .join(' ');

  if (props.variant === 'links') {
    return (
      <nav aria-label={props.label} data-testid={props['data-testid']} className={trackClass}>
        {props.links.map((l) => {
          const itemClass = (isActive: boolean): string =>
            [base, l.wide ? 'flex-[1.5]' : 'flex-1', isActive ? ITEM_ON[tone] : ITEM_OFF[tone]].join(' ');
          if (l.active !== undefined) {
            return (
              <Link
                key={l.to}
                to={l.to}
                // Å bytte fane skal ikke legge igjen historikk man må trykke seg
                // tilbake gjennom.
                replace
                aria-current={l.active ? 'page' : undefined}
                data-testid={l.testId}
                className={itemClass(l.active)}
              >
                {l.label}
              </Link>
            );
          }
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end ?? true}
              data-testid={l.testId}
              className={({ isActive }) => itemClass(isActive)}
            >
              {l.label}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  const { options, value, onChange } = props;
  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      data-testid={props['data-testid']}
      className={trackClass}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={o.testId}
            onClick={() => onChange(o.value)}
            className={[base, 'flex-1', active ? ITEM_ON[tone] : ITEM_OFF[tone]].join(' ')}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
